import { Component, ReactNode } from 'react'
import * as cn from 'classnames'
import * as flatten from 'lodash/flatten'
import { MoneyflowCategoryGroup, TypedMoneyflowCategory } from 'interfaces/meta'
import InlineDropdownWrapper, { InlineDropdownCheckSelected } from 'components/atoms/dropdowns/InlineDropdownWrapper'
import * as styles from 'styles/atoms/dropdowns/InlineDropdownWrapper.css'
import { connect, MapStateToProps } from 'react-redux'
import { categoryGroupsSelector } from 'selectors/globalSelectors'
import { createSelector } from 'reselect'
import { isMobileSelector } from 'selectors/viewportSelectors'
import CategoriesOverlay from 'components/atoms/CategoriesOverlay'

type TransactionCategoryDropdownStateProps = {
  categoryGroups: MoneyflowCategoryGroup[] | null
  isMobile: boolean
}

type TransactionCategoryDropdownOwnProps = {
  selected: TypedMoneyflowCategory

  onSelect: (entity: TypedMoneyflowCategory) => void
}

type TransactionCategoryDropdownProps =
  & TransactionCategoryDropdownStateProps
  & TransactionCategoryDropdownOwnProps

class TransactionCategoryDropdownComponent extends Component<TransactionCategoryDropdownProps> {
  private flattenedCategories: TypedMoneyflowCategory[] | null = null

  constructor (props: TransactionCategoryDropdownProps, context: any) {
    super(props, context)

    this.flattenedCategories = flatten((props.categoryGroups || []).map((group) => group.categories))
  }

  render () {
    const { categoryGroups, selected, isMobile } = this.props

    if (!categoryGroups) {
      return null
    }

    const CategoryInlineDropdown: new () => InlineDropdownWrapper<TypedMoneyflowCategory> = InlineDropdownWrapper as any

    return isMobile
      ? (
        <CategoriesOverlay
          selected={ selected }
          getCaption={ (category: TypedMoneyflowCategory) => category.value }
          checkSelected={ this.checkSelected }
          onSelect={ this.props.onSelect }
        >
          <div className={ styles.isMobile }>
            { categoryGroups.map(this.renderCategoryGroup) }
          </div>
        </CategoriesOverlay>
      )
      : (
        <CategoryInlineDropdown
          selected={ selected }
          withArrow={ false }
          getCaption={ (category: TypedMoneyflowCategory) => category.value }
          checkSelected={ this.checkSelected }
          qaMarker='transaction-category-dropdown'
          onSelect={ this.props.onSelect }
        >
          { categoryGroups.map(this.renderCategoryGroup) }
        </CategoryInlineDropdown>
      )
  }

  renderCategoryGroup = (categoryGroup: MoneyflowCategoryGroup): ReactNode => {
    const { selected } = this.props
    const { id: categoryGroupId, name, categories } = categoryGroup

    const categoryElements = categories.map(({ id, value }) => (
      <li
        key={ String(id) }
        className={ cn(styles.option, {
          [styles.selectedOption]: id === selected.id,
        }) }
      >
        <a className={ styles.optionCaption } data-option-id={ id }>{ value }</a>
      </li>
    ))

    const hintElement = categoryGroupId === 'transfers' ? (
      <div className={ styles.groupHint }>
        Transfers are excluded from totals.
      </div>
    ) : null

    return (
      <div key={ categoryGroupId } className={ styles.group }>
        <div className={ styles.groupName }>{ name }</div>
        <ul className={ styles.groupList }>
          { categoryElements }
        </ul>
        { hintElement }
      </div>
    )
  }

  private checkSelected: InlineDropdownCheckSelected<TypedMoneyflowCategory> = (e) => {
    const { target } = e
    const optionNode = (target as HTMLElement).closest(`[data-option-id]`) as HTMLElement | null
    if (!optionNode) {
      return null
    }

    if (!this.flattenedCategories) {
      return null
    }

    const { optionId } = optionNode.dataset
    const selected = this.flattenedCategories.find(({ id }) => String(id) === optionId)
    if (!selected) {
      return null
    }

    return selected
  }
}

const mapStateToProps: MapStateToProps<TransactionCategoryDropdownStateProps, TransactionCategoryDropdownOwnProps> = createSelector(
  categoryGroupsSelector,
  isMobileSelector,
  (categoryGroups, isMobile) => ({
    categoryGroups,
    isMobile,
  }),
)
const TransactionCategoryDropdown = connect<
  TransactionCategoryDropdownStateProps, {}, TransactionCategoryDropdownOwnProps>(mapStateToProps)(TransactionCategoryDropdownComponent)

export default TransactionCategoryDropdown
